import React, { useContext, Fragment } from 'react';
import { compose } from 'recompose';

import { Grid, TextField, MenuItem, Checkbox, ListSubheader, InputAdornment, IconButton } from '@material-ui/core';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';
import { css } from '@emotion/react';
import { PrimaryButton } from '@/styles/blocks';

import { Context, ContextProvider } from './store';
import { H1, Body1 } from './blocks';
import Table from './Table';

export const AdminPricing = ({ context }) => {
    const { state, loading, callbacks } = useContext(context);

    return (
        <>
            <Grid
                direction="column"
                container
                css={css`
                    width: 100%;
                    height: 100%;
                `}
            >
                <Grid
                    direction="row"
                    item
                    css={css`
                        display: flex;
                        background-color: white;
                        justify-content: flex-end;
                    `}
                >
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            justify-content: center;
                            padding: 18px 16px;
                        `}
                    >
                        <PrimaryButton disabled={!state.isValid} loading={loading.save} onClick={callbacks.save}>
                            Save
                        </PrimaryButton>
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        padding: 40px;
                        padding-bottom: 0;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            justify-content: center;
                        `}
                    >
                        <H1>Pricing</H1>
                    </Grid>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            justify-content: center;
                        `}
                    ></Grid>
                </Grid>

                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        flex-wrap: nowrap;
                        padding: 0 40px;
                        margin-top: 28px;
                        margin-bottom: 28px;
                    `}
                >
                    {state.shipperOptions?.length > 0 ? (
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                                justify-content: center;
                                margin-right: 8px;
                            `}
                        >
                            <TextField
                                select
                                variant="outlined"
                                label="Shipper"
                                size="medium"
                                value={state.shipper || ''}
                                onChange={(e) => {
                                    callbacks.selectShipper(e.target.value);
                                }}
                                fullWidth
                                css={css`
                                    background-color: white;
                                    width: 100%;
                                    padding-right: 2px;
                                    margin-top: 0.2rem;
                                `}
                            >
                                {state.shipperOptions.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        <Body1>{label}</Body1>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    ) : null}
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            justify-content: center;
                            overflow: hidden;
                            margin-left: 8px;
                        `}
                    >
                        <TextField
                            variant="outlined"
                            select
                            fullWidth
                            label="Options"
                            size="medium"
                            value={state?.selected || []}
                            InputLabelProps={{ shrink: true }}
                            disabled={!state.shipper}
                            onChange={(e) => {
                                callbacks.selectOption(e.target.value);
                            }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    return state.selectedObj.map((option) => option.name).join(', ');
                                },
                            }}
                            css={css`
                                background-color: white;
                                width: 100%;
                                padding-right: 2px;
                                margin-top: 0.2rem;
                            `}
                        >
                            {[
                                { label: 'Tier 1', attr: 'TIER_1' },
                                { label: 'Tier 2', attr: 'TIER_2' },
                            ]
                                .filter(({ attr }) => state.pricingOptions[attr].length > 0)
                                .map(({ label, attr }) => {
                                    return [
                                        <ListSubheader>{label}</ListSubheader>,
                                        ...(state?.pricingOptions?.[attr] || []).map(({ label, value }) => {
                                            const selected =
                                                state.selectedObj.find((option) => option.name === label) || {};

                                            return (
                                                <MenuItem
                                                    key={`${attr}-${value}`}
                                                    value={value}
                                                    disabled={
                                                        selected?.name === label &&
                                                        (([
                                                            MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING,
                                                            MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY,
                                                        ].includes(selected.marketplace_tier) &&
                                                            attr === 'TIER_1') ||
                                                            ([MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY].includes(
                                                                selected.marketplace_tier
                                                            ) &&
                                                                attr === 'TIER_2'))
                                                    }
                                                >
                                                    <Checkbox
                                                        color="primary"
                                                        checked={state?.selected.includes(value)}
                                                    />
                                                    <Body1>{label}</Body1>
                                                </MenuItem>
                                            );
                                        }),
                                    ];
                                })}
                        </TextField>
                    </Grid>
                </Grid>

                <Grid
                    item
                    css={css`
                        overflow: hidden;
                        flex-grow: 1;
                        padding: 0 40px;
                        margin-bottom: 40px;
                    `}
                >
                    <Table state={state} loading={loading} callbacks={callbacks} />
                </Grid>
            </Grid>
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} context={Context} />
        </ContextProvider>
    );

export default compose(withContext)(AdminPricing);
